import Vue from 'vue';
import toastr from 'toastr';
import { i18n } from '@/plugins/i18n';
import rootStore from '@/store';
import * as actionsTypes from './action-types';
import * as mutationsTypes from './mutation-types';
// Utils
import { doAsync } from '@/utils/async';
import { SEND_MESSAGE } from '@/store/modules/socket/action-types';
import SocketMessages from '@/utils/classes/SocketMessages';
import ErrorCode from '@/utils/classes/ErrorCode';

/**
 * Check for errors in the response from GW requests
 * @param  {Object} response - XHR response
 * @return {Boolean} true if there's an error
 */
function gwErrorResponse(response) {
  if (response && response.data === ErrorCode.GW_NO_ACCESS_TO_GW) {
    toastr.error(i18n.t('message.GW_NO_ACCESS'));
    return true;
  }
  return false;
}

/**
 * Check the response match a GW object
 * @param  {Object} response - XHR response
 * @return {Object} GW Object
 */
function handleSpecificGwResponse(response, store, displaySaveMsg = true) {
  if (response && response.data instanceof Object) {
    store.commit(mutationsTypes.SET_GW, { data: response.data });
    if (displaySaveMsg) {
      toastr.success(i18n.t('message.GW_SAVED'));
    }
    return response.data;
  } else if (!gwErrorResponse(response)) {
    toastr.error(i18n.t('message.CONNECTION_ERROR'));
  }
  throw response;
}

export default {

  /** Fetch specific GW by specifying its ID */
  async [actionsTypes.FETCH_GW](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/get.php',
      data: payload,
    }).then((response) => handleSpecificGwResponse(response, store, false));
  },

  /** Fetch available GW templates and set GET_GW_TEMPLATES with the current team's templates */
  async [actionsTypes.FETCH_GW_TEMPLATES](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/get.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_GW_TEMPLATES,
      callback: (response) => {
        const data = (response.data instanceof Array ? response.data : [])
          .filter(gw => gw.team === rootStore.state.currentTeam.id);
        Object.freeze(data);
        return data;
      },
    })
      .then((response) => {
        const { data } = response;
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw response;
        }
        store.commit(mutationsTypes.SET_TEAM_GW_TEMPLATES, { payload, data });
        return data;
      });
  },

  /** Fetch all ongoing field tours for this team */
  async [actionsTypes.FETCH_ONGOING_GW](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/get.php',
        data: { ongoing: true },
      },
      mutationTypes: mutationsTypes.GET_ONGOING_GW,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (!(response.data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw response;
        }
        return response.data;
      });
  },

  /** Fetch GW checklists */
  async [actionsTypes.FETCH_GW_CHECKLISTS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/checkLists/get.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_GW_CHECKLISTS,
      callback: (response) => {
        const data = (response.data instanceof Array ? response.data : []);
        Object.freeze(data);
        return data;
      },
    })
      .then((response) => {
        if (!(response.data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw response;
        }
        store.commit(mutationsTypes.SET_TEAM_GW_CHECKLISTS, { data: response.data });
        return response.data;
      });
  },

  /** Save a new field tour or edit an existing one */
  async [actionsTypes.SET_GW](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/set.php',
      data: payload,
    })
      .then((response) => {
        toastr.remove();
        if (response) {
          if (gwErrorResponse(response)) {
            throw response;
          } else if (response.data instanceof Object) {
            rootStore.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.NOTIF_MSG);
            // Update gw-overlay content
            store.dispatch(actionsTypes.FETCH_ONGOING_GW);
            return response;
          } else {
            toastr.error(i18n.t('message.CONNECTION_ERROR'));
          }
        }
        throw response;
      });
  },

  /** Start a field tour */
  async [actionsTypes.START_GW](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/start.php',
      data: payload,
    })
      .then((response) => {
        toastr.remove();
        if (response) {
          if (gwErrorResponse(response)) {
            throw response;
          } else if (response.data === -2) {
            toastr.error(i18n.t('message.GW_NO_STEPS'));
          } else if (response.data instanceof Object) {
            rootStore.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.NOTIF_MSG);
            store.commit(mutationsTypes.SET_GW, { data: response.data });
            // Update gw-overlay content
            store.dispatch(actionsTypes.FETCH_ONGOING_GW);
            return response;
          } else {
            toastr.error(i18n.t('message.CONNECTION_ERROR'));
          }
        }
        throw response;
      });
  },

  /** Delete a field tour template */
  async [actionsTypes.DELETE_GW_TEMPLATE](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/delete.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_GW_TEMPLATES,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        const { data } = response;
        if (gwErrorResponse(response)) {
          throw response;
        } else if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw response;
        }
        store.commit(mutationsTypes.SET_TEAM_GW_TEMPLATES, { data });
        return data;
      });
  },

  /** Delete a field tour */
  async [actionsTypes.DELETE_GW](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/delete.php',
      data: payload,
    })
      .then((response) => {
        toastr.remove();
        if (response && response.data === 1) {
          toastr.success(i18n.t('message.GW_ITEM_DELETED'));
          return 1;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      });
  },

  /** Duplicate a field tour */
  async [actionsTypes.DUPLICATE_GW](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/duplicate.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_GW_TEMPLATES,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        const { data } = response;
        if (gwErrorResponse(response)) {
          throw response;
        } else if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw response;
        }
        store.commit(mutationsTypes.SET_TEAM_GW_TEMPLATES, { data });
        return data;
      });
  },

  /** Create a new GW checklist */
  async [actionsTypes.CREATE_GW_CHECKLIST](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/checkLists/create.php',
      },
      mutationTypes: mutationsTypes.GET_GW_CHECKLISTS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (!(response.data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw response;
        }
        store.commit(mutationsTypes.SET_TEAM_GW_CHECKLISTS, { data: response.data });
        return response.data;
      });
  },

  /** Delete a GW checklist */
  async [actionsTypes.DELETE_GW_CHECKLIST](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/checkLists/delete.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_GW_CHECKLISTS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (!(response.data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw response;
        }
        store.commit(mutationsTypes.SET_TEAM_GW_CHECKLISTS, { data: response.data });
        return response.data;
      });
  },

  /** Edit the name of a GW checklist */
  async [actionsTypes.EDIT_GW_CHECKLIST](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/checkLists/setName.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_GW_CHECKLISTS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (!(response.data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw response;
        }
        toastr.success(i18n.t('message.GW_SAVED'));
        store.commit(mutationsTypes.SET_TEAM_GW_CHECKLISTS, { data: response.data });
        return response.data;
      });
  },

  /** Duplicate a GW checklist along with its items */
  async [actionsTypes.DUPLICATE_GW_CHECKLIST](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/checkLists/duplicate.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_GW_CHECKLISTS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (!(response.data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw response;
        }
        toastr.success(i18n.t('message.GW_SAVED'));
        store.commit(mutationsTypes.SET_TEAM_GW_CHECKLISTS, { data: response.data });
        return response.data;
      });
  },

  /** Edit/create a GW step */
  async [actionsTypes.SET_GW_STEP](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/steps/set.php',
      data: payload,
    }).then((response) => handleSpecificGwResponse(response, store));
  },

  /** Delete a GW step */
  async [actionsTypes.DELETE_GW_STEP](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/steps/delete.php',
      data: payload,
    }).then((response) => handleSpecificGwResponse(response, store));
  },

  /** Edit/create a GW planning */
  async [actionsTypes.SET_GW_PLANNING](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/plannings/set.php',
      data: payload,
    }).then((response) => handleSpecificGwResponse(response, store));
  },

  /** Delete a GW planning */
  async [actionsTypes.DELETE_GW_PLANNING](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/plannings/delete.php',
      data: payload,
    }).then((response) => handleSpecificGwResponse(response, store));
  },

  /** Create/Update a comment of a checklist item */
  async [actionsTypes.SET_GW_CL_ITEM_COMMENT](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/checkLists/comments/set.php',
      data: payload,
    }).then((response) => handleSpecificGwResponse(response, store));
  },

  /** Delete a comment of a checklist item */
  async [actionsTypes.DELETE_GW_CL_ITEM_COMMENT](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/checkLists/comments/delete.php',
      data: payload,
    }).then((response) => handleSpecificGwResponse(response, store));
  },

  /** Validate/Invalidate a step */
  async [actionsTypes.VALIDATE_STEP](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/okStep.php',
      data: payload,
    }).then((response) => handleSpecificGwResponse(response, store));
  },

  /** Set OK/NOK/NA on a checklist item */
  async [actionsTypes.TICK_ITEM](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/gembaWalks/checkLists/ok.php',
      data: payload,
    }).then((response) => handleSpecificGwResponse(response, store));
  },

};
